import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../components/layout/MainLayout';
import { ChevronLeft, Plus, Edit, Trash2, LayoutTemplate, FileText, Paperclip, X } from 'lucide-react';
import api from '../../../utils/api';
import MediaPickerModal from '../../../components/shared/MediaPickerModal';

const EmailTemplateManager = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [latestLead, setLatestLead] = useState(null);
  const [loading, setLoading] = useState(true);

  // Editor State
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  // Variable Picker State
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [pickerFilter, setPickerFilter] = useState('');
  const [activeField, setActiveField] = useState(null); // 'subject' or 'body'
  const [cursorPos, setCursorPos] = useState(0);

  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  const fetchTemplates = async () => {
    try {
      const res = await api('/email-templates');
      if (res.ok) {
        setTemplates(await res.json());
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchLatestLead = async () => {
    try {
      // Get at least 1 lead to use as sample data
      const res = await api('/leads?limit=1');
      if (res.ok) {
        const leads = await res.json();
        if (leads.length > 0) {
          setLatestLead(leads[0]);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    Promise.all([fetchTemplates(), fetchLatestLead()]).finally(() => setLoading(false));
  }, []);

  // Compute available variables from latestLead
  const getAvailableVariables = () => {
    let vars = ['lead_name', 'lead_phone', 'lead_email', 'lead_source', 'lead_location', 'lead_status', 'lead_type'];
    if (latestLead && latestLead.customFields) {
      Object.keys(latestLead.customFields).forEach(key => {
        if (!vars.includes(key)) vars.push(key);
      });
    }
    return vars;
  };

  const handleInputChange = (e, field) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    
    if (field === 'subject') setSubject(val);
    if (field === 'body') setBody(val);

    // Check for '{{' trigger
    const textBeforeCursor = val.slice(0, pos);
    const triggerMatch = textBeforeCursor.match(/\{\{([^{}]*)$/);

    if (triggerMatch) {
      setActiveField(field);
      setCursorPos(pos);
      setPickerFilter(triggerMatch[1].toLowerCase());
      setShowPicker(true);
      
      // Calculate rough position
      const rect = e.target.getBoundingClientRect();
      // Use simple positioning relative to input box
      setPickerPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX + Math.min((pos * 8), rect.width - 200) // approx cursor x
      });
    } else {
      setShowPicker(false);
    }
  };

  const insertVariable = (varName) => {
    const val = activeField === 'subject' ? subject : body;
    const textBeforeCursor = val.slice(0, cursorPos);
    const textAfterCursor = val.slice(cursorPos);
    
    // Find the '{{' and replace it with '{{varName}}'
    const lastTriggerIndex = textBeforeCursor.lastIndexOf('{{');
    
    if (lastTriggerIndex !== -1) {
      const newTextBefore = textBeforeCursor.slice(0, lastTriggerIndex) + `{{${varName}}}`;
      const newVal = newTextBefore + textAfterCursor;
      
      if (activeField === 'subject') {
        setSubject(newVal);
      } else {
        setBody(newVal);
      }
    }
    
    setShowPicker(false);
    
    // Focus back
    setTimeout(() => {
      const ref = activeField === 'subject' ? subjectRef.current : bodyRef.current;
      if (ref) {
        ref.focus();
        const newPos = lastTriggerIndex + varName.length + 4; // '{{}}'
        ref.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const filteredVars = getAvailableVariables().filter(v => v.toLowerCase().includes(pickerFilter));

  const handleSave = async () => {
    if (!name || !subject || !body) return alert("All fields are required");

    try {
      const payload = { name, subject, body, attachments };
      let res;
      if (editingId) {
        res = await api(`/email-templates/${editingId}`, { method: 'PUT', body: payload });
      } else {
        res = await api('/email-templates', { method: 'POST', body: payload });
      }

      if (res.ok) {
        alert("Template saved successfully!");
        setEditingId(null);
        setName('');
        setSubject('');
        setBody('');
        setAttachments([]);
        fetchTemplates();
      } else {
        const err = await res.json();
        alert(err.message || "Failed to save template");
      }
    } catch (err) {
      alert("Error saving template");
    }
  };

  const handleEdit = (t) => {
    setEditingId(t._id);
    setName(t.name);
    setSubject(t.subject);
    setBody(t.body);
    setAttachments(t.attachments || []);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;
    try {
      const res = await api(`/email-templates/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchTemplates();
        if (editingId === id) {
          setEditingId(null);
          setName('');
          setSubject('');
          setBody('');
          setAttachments([]);
        }
      }
    } catch (err) {
      alert("Error deleting template");
    }
  };

  const handleNew = () => {
    setEditingId(null);
    setName('');
    setSubject('');
    setBody('');
    setAttachments([]);
  };

  const handleAttachFile = (file) => {
    if (attachments.some(a => a.fileId === file._id)) return;
    setAttachments([...attachments, { fileId: file._id, fileName: file.fileName, fileUrl: file.fileUrl }]);
  };

  const handleRemoveAttachment = (fileId) => {
    setAttachments(attachments.filter(a => a.fileId !== fileId));
  };

  // Preview Logic
  const renderPreview = (text) => {
    if (!text) return '';
    return text.replace(/\{\{([^}]+)\}\}/g, (match, p1) => {
      if (!latestLead) return match;
      
      let val = '';
      switch(p1) {
        case 'lead_name': val = latestLead.name; break;
        case 'lead_phone': val = latestLead.phone; break;
        case 'lead_email': val = latestLead.email; break;
        case 'lead_source': val = latestLead.source; break;
        case 'lead_location': val = latestLead.location; break;
        case 'lead_status': val = latestLead.status; break;
        case 'lead_type': val = latestLead.leadType; break;
        default: 
          val = (latestLead.customFields && latestLead.customFields[p1]) ? latestLead.customFields[p1] : `[${p1} not found]`;
          break;
      }
      return val || `[${p1} empty]`;
    });
  };

  if (loading) return <MainLayout><div style={{padding:'24px'}}>Loading...</div></MainLayout>;

  return (
    <MainLayout title="Email Templates">
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button 
          onClick={() => navigate('/automation?tab=email')}
          style={{ background: 'white', border: '1px solid var(--color-border-primary)', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '500' }}
        >
          <ChevronLeft size={16} /> Back to Hub
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px', alignItems: 'start' }}>
        {/* Left Panel: List */}
        <div className="card" style={{ background: 'white', borderRadius: '8px', border: '1px solid var(--color-border-primary)', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid var(--color-border-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>Saved Templates</h3>
            <button onClick={handleNew} style={{ background: 'var(--color-brand-primary)', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
              <Plus size={14} /> New
            </button>
          </div>
          <div style={{ padding: '16px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {templates.length === 0 ? (
              <div style={{ color: 'var(--color-text-tertiary)', textAlign: 'center', padding: '24px 0', fontSize: '13px' }}>No templates found. Create one!</div>
            ) : (
              templates.map(t => (
                <div key={t._id} style={{ border: `1px solid ${editingId === t._id ? 'var(--color-brand-primary)' : 'var(--color-border-primary)'}`, borderRadius: '6px', padding: '12px', background: editingId === t._id ? '#F0F7FF' : 'white' }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>{t.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t.subject}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => handleEdit(t)} style={{ flex: 1, background: 'white', border: '1px solid var(--color-border-primary)', padding: '6px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '12px' }}>
                      <Edit size={12} /> Edit
                    </button>
                    <button onClick={() => handleDelete(t._id)} style={{ background: '#FFF1F0', color: '#E53E3E', border: '1px solid #FED7D7', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel: Editor & Preview */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div className="card" style={{ background: 'white', padding: '24px', borderRadius: '8px', border: '1px solid var(--color-border-primary)' }}>
            <h2 style={{ margin: '0 0 24px 0', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LayoutTemplate size={20} color="var(--color-brand-primary)" />
              {editingId ? 'Edit Template' : 'Create New Template'}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)', marginBottom: '6px' }}>Template Name</label>
                <input 
                  type="text" 
                  value={name} 
                  onChange={e => setName(e.target.value)} 
                  placeholder="e.g. Follow-up Email"
                  style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid var(--color-border-primary)', outline: 'none' }}
                />
              </div>

              <div style={{ position: 'relative' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Email Subject</span>
                  <span style={{ fontWeight: 'normal', color: 'var(--color-text-tertiary)' }}>Type {'{{'} for variables</span>
                </label>
                <input 
                  ref={subjectRef}
                  type="text" 
                  value={subject} 
                  onChange={e => handleInputChange(e, 'subject')} 
                  onKeyDown={e => { if (e.key === 'Escape') setShowPicker(false); }}
                  placeholder="Subject line..."
                  style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid var(--color-border-primary)', outline: 'none' }}
                />
              </div>

              <div style={{ position: 'relative' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Email Body</span>
                  <span style={{ fontWeight: 'normal', color: 'var(--color-text-tertiary)' }}>Type {'{{'} for variables</span>
                </label>
                <textarea 
                  ref={bodyRef}
                  rows="8"
                  value={body} 
                  onChange={e => handleInputChange(e, 'body')} 
                  onKeyDown={e => { if (e.key === 'Escape') setShowPicker(false); }}
                  placeholder="Write your message here..."
                  style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid var(--color-border-primary)', outline: 'none', resize: 'vertical', fontFamily: 'inherit', marginBottom: '8px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>Attachments</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {attachments.map(att => (
                    <div key={att.fileId} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#F1F5F9', padding: '6px 10px', borderRadius: '4px', fontSize: '12px', color: '#334155', border: '1px solid #E2E8F0' }}>
                      <Paperclip size={12} />
                      <span style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.fileName}</span>
                      <button onClick={() => handleRemoveAttachment(att.fileId)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#94A3B8' }}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  <button 
                    onClick={() => setShowMediaPicker(true)}
                    style={{ background: 'white', border: '1px dashed var(--color-brand-primary)', color: 'var(--color-brand-primary)', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Plus size={14} /> Add Attachment
                  </button>
                </div>
              </div>

              <button 
                onClick={handleSave}
                style={{ alignSelf: 'flex-start', background: 'var(--color-brand-primary)', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '4px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                Save Template
              </button>
            </div>
          </div>

          {/* Live Preview Panel */}
          <div className="card" style={{ background: '#F8FAFC', padding: '24px', borderRadius: '8px', border: '1px solid var(--color-border-primary)' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-text-secondary)' }}>
              <FileText size={16} /> Live Preview
            </h3>
            
            {!latestLead && (
              <div style={{ fontSize: '12px', color: '#D97706', background: '#FEF3C7', padding: '8px 12px', borderRadius: '4px', marginBottom: '16px' }}>
                No leads found in database. Variables will show as raw text.
              </div>
            )}

            <div style={{ background: 'white', borderRadius: '6px', border: '1px solid var(--color-border-tertiary)', overflow: 'hidden' }}>
              <div style={{ padding: '16px', borderBottom: '1px solid var(--color-border-tertiary)', background: '#F1F5F9' }}>
                <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', marginBottom: '4px' }}>Subject</div>
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#0F172A' }}>
                  {renderPreview(subject) || <span style={{ color: '#94A3B8', fontWeight: 'normal' }}>No subject</span>}
                </div>
              </div>
              <div style={{ padding: '20px', fontSize: '14px', color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap', minHeight: '100px' }}>
                {renderPreview(body) || <span style={{ color: '#94A3B8' }}>Start typing your body to see preview...</span>}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Variable Picker Dropdown */}
      {showPicker && (
        <div style={{
          position: 'absolute',
          top: pickerPosition.top + 10,
          left: pickerPosition.left,
          background: 'white',
          border: '1px solid var(--color-border-primary)',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          width: '240px',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1000
        }}>
          <div style={{ padding: '8px', fontSize: '11px', fontWeight: '600', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', borderBottom: '1px solid var(--color-border-tertiary)', background: '#F8FAFC' }}>
            Select a Variable
          </div>
          {filteredVars.length === 0 ? (
            <div style={{ padding: '10px', fontSize: '13px', color: 'var(--color-text-tertiary)', textAlign: 'center' }}>No matches</div>
          ) : (
            filteredVars.map(v => (
              <div 
                key={v} 
                onClick={() => insertVariable(v)}
                style={{ padding: '8px 12px', fontSize: '13px', cursor: 'pointer', borderBottom: '1px solid var(--color-border-tertiary)' }}
                onMouseOver={e => e.currentTarget.style.background = '#F1F5F9'}
                onMouseOut={e => e.currentTarget.style.background = 'white'}
              >
                <span style={{ color: 'var(--color-brand-primary)', fontWeight: '500', marginRight: '8px' }}>{`{{${v}}}`}</span>
              </div>
            ))
          )}
        </div>
      )}

      <MediaPickerModal 
        isOpen={showMediaPicker} 
        onClose={() => setShowMediaPicker(false)} 
        onSelect={handleAttachFile} 
      />

    </MainLayout>
  );
};

export default EmailTemplateManager;
